import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/i18n/LanguageContext";

const SITE = "https://margtor.africa";
const API = process.env.REACT_APP_BACKEND_URL;
const LANGS = ["en", "sw", "pl", "fr", "zh"];
const OG_LOCALE = { en: "en_KE", sw: "sw_KE", pl: "pl_PL", fr: "fr_CM", zh: "zh_CN" };
const LICENSE_URL = "https://creativecommons.org/licenses/by/4.0/";
const ORG = { "@id": `${SITE}/#organization` };
const IMG_META = {
  creator: { "@type": "Organization", name: "MargTor Ltd.", url: SITE },
  creditText: "MargTor Ltd.",
  copyrightNotice: "© MargTor Ltd. — Arthur Toren group",
  acquireLicensePage: `${SITE}/#contact`,
};
const imageObject = (url, extra = {}) => ({ "@type": "ImageObject", url, contentUrl: url, license: LICENSE_URL, ...IMG_META, ...extra });

const langUrl = (path, l) => `${SITE}${path}${l === "en" ? "" : `?lang=${l}`}`;

const upsert = (selector, create, attrs) => {
  let el = document.head.querySelector(selector);
  if (!el) {
    el = create();
    document.head.appendChild(el);
  }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
};
const meta = (key, value, attr = "name") =>
  upsert(`meta[${attr}="${key}"]`, () => document.createElement("meta"), { [attr]: key, content: value });
const link = (rel, href, extra = {}) => {
  const sel = `link[rel="${rel}"]${extra.hreflang ? `[hreflang="${extra.hreflang}"]` : ""}${extra.type ? `[type="${extra.type}"]` : ""}`;
  return upsert(sel, () => document.createElement("link"), { rel, href, ...extra });
};

const homeGraph = (t, lang) => [
  {
    "@type": "Organization", "@id": "https://arthurtoren.co.uk/#organization", name: "Arthur Toren", legalName: "Arthur Toren Associates Ltd.",
    url: "https://arthurtoren.co.uk", sameAs: ["https://arthurtoren.co.uk", "https://linkedin.com/company/arthur-toren-associates"], email: "office@margtor.africa",
    description: t.group.desc, foundingDate: "2019", areaServed: ["KE", "TZ", "GB", "PL"], subOrganization: [ORG, ...t.group.companies.slice(2, 7).map((c) => ({ "@type": "Organization", name: c.name, url: c.url }))],
  },
  {
    "@type": "NewsArticle", "@id": "https://xyz.pl/pierwszy-rok-polskiej-strefy-ekonomicznej-w-kenii-polacy-produkuja-tuk-tuki/#article", headline: t.press.headline, inLanguage: "pl",
    url: "https://xyz.pl/pierwszy-rok-polskiej-strefy-ekonomicznej-w-kenii-polacy-produkuja-tuk-tuki/", publisher: { "@type": "Organization", name: "XYZ", url: "https://xyz.pl" }, about: ORG,
  },
  ...t.products.items.map((p) => ({
    "@type": "Service", "@id": `${SITE}/#service-${p.key}`, name: p.name, description: p.desc, serviceType: p.name,
    provider: ORG, areaServed: ["KE", "CD", "TZ"], url: `${langUrl("/", lang)}#solutions`,
  })),
  {
    "@type": "FAQPage", "@id": `${SITE}/#faq`, inLanguage: lang,
    mainEntity: t.financing.faq.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
  },
  ...t.founders.items.map((f, i) => ({
    "@type": "Person", "@id": `${SITE}/#${i === 0 ? "gurinder-oberoi-singh" : "gregory-radek"}`, name: f.name, jobTitle: f.role,
    description: f.bio, image: imageObject(`${SITE}${f.photo}`, { caption: f.name }), email: f.email, worksFor: ORG, knowsAbout: [...f.stats, ...(i === 1 ? ["Electric two-wheelers", "Electric three-wheelers", "Battery swapping", "EV fleet telematics", "East Africa e-mobility"] : [])],
    ...(f.linkedin ? { sameAs: [...new Set([f.linkedin, ...f.links.map((l) => l.url)])] } : {}),
  })),
  imageObject(`${SITE}/assets/img/boda-hero.jpg`, { "@id": `${SITE}/#hero-image`, caption: `${t.hero.l1} ${t.hero.l2} ${t.hero.l3}` }),
];

const partnersGraph = (t, lang, pageUrl) => [{
  "@type": "HowTo", "@id": `${SITE}/partners#engagement-path`, name: t.partners.title, description: t.partners.desc, inLanguage: lang,
  step: t.partners.steps.map((s, i) => ({ "@type": "HowToStep", position: i + 1, name: s.title, text: s.desc, url: `${pageUrl}#engagement` })),
}];

const knowledgeGraph = (lang, kb) => {
  if (!kb) return [];
  return [
    {
      "@type": "CollectionPage", "@id": `${SITE}/knowledge#collection`, name: "MargTor Knowledge Hub", inLanguage: lang, license: LICENSE_URL,
      hasPart: [...kb.articles.map((a) => ({ "@type": "TechArticle", "@id": `${SITE}/knowledge/${a.slug}#article`, headline: a.title, url: a.url })),
        ...(kb.notes || []).map((n) => ({ "@type": "BlogPosting", "@id": `${SITE}/knowledge/notes/${n.slug}#article`, headline: n.title, url: n.url, author: n.author }))],
    },
    {
      "@type": "DefinedTermSet", "@id": `${SITE}/knowledge#glossary`, name: "EV & e-mobility glossary — East Africa", inLanguage: lang, license: LICENSE_URL,
      hasDefinedTerm: kb.glossary.map((g) => ({ "@type": "DefinedTerm", "@id": `${SITE}/knowledge#term-${g.id}`, name: g.term, description: g.definition, inDefinedTermSet: `${SITE}/knowledge#glossary` })),
    },
    ...(kb.datasets || []).map((d) => ({
      "@type": "Dataset", "@id": `${SITE}/api/datasets/${d.key}.json`, name: d.name, description: d.description, url: `${SITE}/knowledge#datasets`,
      sameAs: d.json, license: LICENSE_URL, isAccessibleForFree: true, inLanguage: "en", creator: ORG, publisher: ORG, dateModified: "2026-06-12",
      spatialCoverage: ["Kenya", "Tanzania", "Democratic Republic of the Congo"], keywords: ["electric mobility", "East Africa", "battery swapping", "TCO", d.key],
      distribution: [
        { "@type": "DataDownload", encodingFormat: "application/json", contentUrl: d.json },
        { "@type": "DataDownload", encodingFormat: "text/csv", contentUrl: d.csv },
      ],
    })),
  ];
};

const articleGraph = (lang, art) => {
  if (!art) return [];
  return [{
    "@type": art.kind === "note" ? "BlogPosting" : "TechArticle", "@id": `${art.url.split("?")[0]}#article`, headline: art.title, description: art.summary, inLanguage: lang,
    url: art.url, dateModified: art.updated, datePublished: art.updated, keywords: art.tags.join(", "), license: LICENSE_URL, isAccessibleForFree: true,
    author: art.author ? { ...art.author, worksFor: ORG } : ORG, publisher: ORG, image: imageObject(`${SITE}/og-image.jpg`, { width: 1200, height: 630, caption: art.title }), mainEntityOfPage: art.url, wordCount: art.body.split(/\s+/).length,
    articleBody: art.body, encoding: { "@type": "MediaObject", encodingFormat: "text/markdown", contentUrl: art.markdown_url },
    about: ["Electric mobility", "East Africa", ...art.tags],
  }];
};

const buildGraph = (t, lang, path, page, extra) => {
  const pageUrl = langUrl(path, lang);
  const crumbs = [{ "@type": "ListItem", position: 1, name: t.seo.breadcrumbHome, item: langUrl("/", lang) }];
  if (path.startsWith("/knowledge")) crumbs.push({ "@type": "ListItem", position: 2, name: t.seo.knowledge.breadcrumb, item: langUrl("/knowledge", lang) });
  if (path.startsWith("/knowledge/notes/")) crumbs.push({ "@type": "ListItem", position: 3, name: t.knowledge.notes.eyebrow, item: langUrl("/knowledge", lang) + "#notes" });
  if (path.startsWith("/knowledge/") && extra) crumbs.push({ "@type": "ListItem", position: crumbs.length + 1, name: extra.title, item: pageUrl });
  if (path === "/partners" || path === "/press") crumbs.push({ "@type": "ListItem", position: 2, name: page.breadcrumb, item: pageUrl });

  const graph = [
    {
      "@type": "WebPage", "@id": `${pageUrl}#webpage`, url: pageUrl, name: page.title, description: page.description, inLanguage: lang,
      isPartOf: { "@id": `${SITE}/#website` }, about: ORG, license: LICENSE_URL,
      primaryImageOfPage: imageObject(`${SITE}/og-image.jpg`, { width: 1200, height: 630, caption: page.ogAlt }),
    },
    { "@type": "BreadcrumbList", "@id": `${pageUrl}#breadcrumb`, itemListElement: crumbs },
  ];
  if (path === "/") graph.push(...homeGraph(t, lang));
  else if (path === "/partners") graph.push(...partnersGraph(t, lang, pageUrl));
  else if (path === "/knowledge") graph.push(...knowledgeGraph(lang, extra));
  else if (path.startsWith("/knowledge/")) graph.push(...articleGraph(lang, extra));
  return { "@context": "https://schema.org", "@graph": graph };
};

export const Seo = () => {
  const { lang, t } = useLang();
  const { pathname } = useLocation();
  const path = pathname.replace(/\/+$/, "") || "/";
  const [loaded, setLoaded] = useState(null);
  const extra = loaded && loaded.path === path && loaded.lang === lang ? loaded.data : null;

  useEffect(() => {
    const keep = (data) => data && setLoaded({ path, lang, data });
    if (path === "/knowledge") fetch(`${API}/api/knowledge?lang=${lang}`).then((r) => r.json()).then(keep).catch(() => {});
    else if (path.startsWith("/knowledge/")) fetch(`${API}/api${path}?lang=${lang}`).then((r) => (r.ok ? r.json() : null)).then(keep).catch(() => {});
  }, [path, lang]);

  useEffect(() => {
    let page = t.seo.home;
    if (path === "/partners") page = t.seo.partners;
    else if (path === "/press") page = t.seo.press;
    else if (path === "/knowledge") page = t.seo.knowledge;
    else if (path.startsWith("/knowledge/")) page = extra ? { title: extra.title, description: extra.summary, ogAlt: t.seo.knowledge.ogAlt } : t.seo.knowledge;

    const url = langUrl(path, lang);
    const fullTitle = `${page.title} | MargTor Ltd.`;

    document.documentElement.lang = lang;
    document.title = fullTitle;
    const current = new URL(window.location.href);
    if (lang === "en") current.searchParams.delete("lang");
    else current.searchParams.set("lang", lang);
    window.history.replaceState(window.history.state, "", current);

    meta("description", page.description);
    meta("keywords", t.seo.keywords);
    link("canonical", url);
    LANGS.forEach((l) => link("alternate", langUrl(path, l), { hreflang: l }));
    link("alternate", langUrl(path, "en"), { hreflang: "x-default" });
    link("license", LICENSE_URL);
    const mdAlt = path.startsWith("/knowledge/") && extra ? extra.markdown_url
      : path === "/partners" ? `${SITE}/api/pages/partners.md` : path === "/press" ? `${SITE}/assets/press/boilerplate.md` : path === "/knowledge" ? `${SITE}/api/knowledge.md?lang=${lang}` : `${SITE}/api/pages/home.md`;
    link("alternate", mdAlt, { type: "text/markdown", title: "Markdown version for AI agents" });
    link("alternate", `${SITE}/llms.txt`, { type: "text/plain", title: "llms.txt" });
    link("alternate", `${SITE}/api/feed.xml`, { type: "application/atom+xml", title: "MargTor Knowledge Hub" });
    link("alternate", `${SITE}/api/feed.json`, { type: "application/feed+json", title: "MargTor Knowledge Hub (JSON Feed)" });
    link("alternate", `${SITE}/.well-known/agent-card.json`, { type: "application/json", title: "Agent card (A2A / MCP)" });

    meta("og:type", path.startsWith("/knowledge/") ? "article" : "website", "property");
    meta("og:url", url, "property");
    meta("og:title", fullTitle, "property");
    meta("og:description", page.description, "property");
    meta("og:locale", OG_LOCALE[lang], "property");
    meta("og:image:alt", page.ogAlt, "property");
    meta("twitter:title", fullTitle);
    meta("twitter:description", page.description);
    meta("twitter:image:alt", page.ogAlt);

    let script = document.getElementById("ld-json-page");
    if (!script) {
      script = document.createElement("script");
      script.id = "ld-json-page";
      script.type = "application/ld+json";
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(buildGraph(t, lang, path, page, extra));
  }, [lang, t, path, extra]);

  return null;
};
